import { Inject, Injectable } from '@angular/core';
import { USER_TYPES } from '../../enums/user-types.enum';
import { ParticipantUtilityService } from '../../services/participant/participant-utility.service';
import { WINDOW } from '../../providers/window.provider';
import { ClientLoginDetails, Participant } from '@edassist/api';
import { PendoMetaData } from '../../models/pendo/pendo-meta-data.interface';
import { constants, LocalStorageService } from '@edassist/common';

@Injectable({ providedIn: 'root' })
export class PendoService {
	private readonly PENDO_ROLE_PREFIX = 'ea-';

	constructor(@Inject(WINDOW) private readonly window: Window) {}

	initialize(
		participant?: Participant,
		clientDetails?: ClientLoginDetails,
		isAppealDesignee = false
	): void {
		const pendoMetaData: PendoMetaData = {
			visitor: {
				id:
					(
						ParticipantUtilityService.getConsolidatedLoginUserId() ||
						participant
					) ?
						(
							ParticipantUtilityService.getConsolidatedLoginUserId() ||
							participant.id
						).toString()
					:	'',
				role:
					isAppealDesignee ? `${this.PENDO_ROLE_PREFIX}appeal-designee`
					: participant ?
						this.getPendoUserRoleFromUserType(participant.user.userType.id)
					:	'',
				first_name:
					participant ? participant.user.firstName
					: (
						LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER)
					) ?
						LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER)
							?.firstName
					:	'',
				last_name:
					participant ? participant.user.lastName
					: (
						LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER)
					) ?
						LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER)
							?.lastName
					:	'',
				email:
					participant ? participant.preferEmail || participant.otherEmail : '',
				...(!participant && {
					username:
						(
							LocalStorageService.getItem(
								constants.localStorageKeys.CURRENT_USER
							)
						) ?
							LocalStorageService.getItem(
								constants.localStorageKeys.CURRENT_USER
							)?.username
						:	''
				}),
				...(!participant && {
					userTypeId:
						(
							LocalStorageService.getItem(
								constants.localStorageKeys.CURRENT_USER
							)
						) ?
							LocalStorageService.getItem(
								constants.localStorageKeys.CURRENT_USER
							)?.userTypeId
						:	''
				}),
				participantId: participant ? participant.id.toString() : ''
			},
			account: {
				id:
					clientDetails?.crmClientId ?
						clientDetails?.crmClientId.toString().toLowerCase() // Forcing CRM client ID to be all lowercase for pendo reporting purposes.
					:	''
			}
		};
		// Ignore typescript error saying pendo is not on window object, it will be and is just not part of default
		// DOM Window interface
		(this.window as any).pendo?.initialize(pendoMetaData);
	}

	updateOptions(
		participant?: Participant,
		clientDetails?: ClientLoginDetails,
		isAppealDesignee = false,
		applicationData?: any
	): void {
		const pendoMetaData: PendoMetaData = {
			visitor: {
				id:
					(
						ParticipantUtilityService.getConsolidatedLoginUserId() ||
						participant
					) ?
						(
							ParticipantUtilityService.getConsolidatedLoginUserId() ||
							participant.id
						).toString()
					:	'',
				role:
					isAppealDesignee ? `${this.PENDO_ROLE_PREFIX}appeal-designee`
					: participant ?
						this.getPendoUserRoleFromUserType(participant.user.userType.id)
					:	'',
				first_name:
					participant ? participant.user.firstName
					: (
						LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER)
					) ?
						LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER)
							?.firstName
					:	'',
				last_name:
					participant ? participant.user.lastName
					: (
						LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER)
					) ?
						LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER)
							?.lastName
					:	'',
				email:
					participant ? participant.preferEmail || participant.otherEmail : '',
				username:
					LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER) ?
						LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER)
							?.username
					:	'',
				userTypeId:
					LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER) ?
						LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER)
							?.userTypeId
					:	'',
				applicationId: applicationData.id,
				applicationNumber: applicationData.number,
				programId: applicationData.programId,
				programName: applicationData.programName,
				providerName: applicationData.providerName,
				participantId: participant ? participant.id.toString() : ''
			},
			account: {
				id:
					clientDetails?.crmClientId ?
						clientDetails?.crmClientId.toString().toLowerCase() // Forcing CRM client ID to be all lowercase for pendo reporting purposes.
					:	''
			}
		};
		(this.window as any).pendo?.updateOptions(pendoMetaData);
	}

	flushNow(): void {
		(this.window as any).pendo?.flushNow();
	}

	private getPendoUserRoleFromUserType(userTypeId: number): string {
		switch (userTypeId) {
			case USER_TYPES.Participant:
				return `${this.PENDO_ROLE_PREFIX}participant`;
			case USER_TYPES.ClientAdministrativeAccess:
				return `${this.PENDO_ROLE_PREFIX}client-admin`;
			case USER_TYPES.Supervisor:
				return `${this.PENDO_ROLE_PREFIX}supervisor`;
			default:
				return '';
		}
	}
}
